/**
 * Idea-tags store. Holds the account's tag catalog and every idea↔tag link, so
 * the tag picker (idea capture, Command Center), the per-idea chips, and the
 * filter bar on every idea list all read from one place. Loaded once per
 * session, like the vocabulary store.
 *
 * There is no central ideas store in this app — each list page fetches its own
 * ideas — so tags deliberately live here, keyed by ideaId, rather than being
 * threaded through every list method's response. A page renders its own ideas
 * and asks this store which tags each carries and how to filter.
 *
 * Every mutating backend method returns the fresh catalog, so writes reconcile
 * the whole store in one round-trip; optimistic updates make the UI feel instant
 * and are replaced by server truth the moment the call returns.
 */

import { create } from 'zustand';
import api from '../api';

export interface IdeaTag {
  id: string;
  name: string;
  /** How many of the account's ideas currently carry this tag. */
  ideaCount: number;
  lastUsedAt: number;
}

interface TagCatalog {
  tags: IdeaTag[];
  links: Array<{ ideaId: string; tagId: string }>;
  collections: Array<{ tagId: string; position: number }>;
}

interface TagsState {
  loaded: boolean;
  loading: boolean;
  /** Catalog, newest-used first (server order). */
  tags: IdeaTag[];
  tagsById: Map<string, IdeaTag>;
  /** ideaId -> tagId[] */
  tagIdsByIdea: Map<string, string[]>;
  /** Pinned Collections, in sidebar order (tag ids). */
  collectionTagIds: string[];

  ensureLoaded: () => Promise<void>;
  reload: () => Promise<void>;
  /** The tags a given idea carries, in catalog order. */
  tagsForIdea: (ideaId: string) => IdeaTag[];
  /** Tags actually in use (ideaCount > 0), the set the filter bar shows. */
  activeTags: () => IdeaTag[];
  /** Pinned Collections as tag objects, in sidebar order. */
  collectionTags: () => IdeaTag[];
  isPinned: (tagId: string) => boolean;

  setIdeaTags: (ideaId: string, tagNames: string[]) => Promise<void>;
  renameTag: (tagId: string, name: string) => Promise<void>;
  deleteTag: (tagId: string) => Promise<void>;
  pinCollection: (tagId: string) => Promise<void>;
  unpinCollection: (tagId: string) => Promise<void>;
  reorderCollections: (orderedTagIds: string[]) => Promise<void>;
  reset: () => void;
}

/** Rebuild the derived maps from a catalog payload. */
function indexCatalog(catalog: TagCatalog) {
  const tagsById = new Map<string, IdeaTag>();
  for (const t of catalog.tags) tagsById.set(t.id, t);
  const tagIdsByIdea = new Map<string, string[]>();
  for (const l of catalog.links) {
    const arr = tagIdsByIdea.get(l.ideaId);
    if (arr) arr.push(l.tagId);
    else tagIdsByIdea.set(l.ideaId, [l.tagId]);
  }
  const collectionTagIds = [...(catalog.collections ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((c) => c.tagId);
  return { tags: catalog.tags, tagsById, tagIdsByIdea, collectionTagIds };
}

const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();

export const useTags = create<TagsState>((set, get) => ({
  loaded: false,
  loading: false,
  tags: [],
  tagsById: new Map(),
  tagIdsByIdea: new Map(),
  collectionTagIds: [],

  ensureLoaded: async () => {
    const { loaded, loading } = get();
    if (loaded || loading) return;
    set({ loading: true });
    try {
      const catalog = await api.getIdeaTags();
      set({ ...indexCatalog(catalog), loaded: true, loading: false });
    } catch {
      // Transient failure: leave the store empty rather than blocking the page.
      set({ loaded: true, loading: false });
    }
  },

  reload: async () => {
    try {
      const catalog = await api.getIdeaTags();
      set({ ...indexCatalog(catalog), loaded: true });
    } catch {
      /* keep prior state */
    }
  },

  tagsForIdea: (ideaId) => {
    const ids = get().tagIdsByIdea.get(ideaId) ?? [];
    // Preserve catalog order (newest-used first) rather than link insertion order.
    return get().tags.filter((t) => ids.includes(t.id));
  },

  activeTags: () => get().tags.filter((t) => t.ideaCount > 0),

  collectionTags: () => {
    const { collectionTagIds, tagsById } = get();
    return collectionTagIds
      .map((id) => tagsById.get(id))
      .filter((t): t is IdeaTag => !!t);
  },

  isPinned: (tagId) => get().collectionTagIds.includes(tagId),

  setIdeaTags: async (ideaId, tagNames) => {
    // Optimistic: resolve requested names to existing tag ids where possible and
    // mint temporary ids for brand-new names, so the UI updates before the round
    // trip. The server response replaces this with real ids + counts.
    const before = {
      tags: get().tags,
      tagsById: get().tagsById,
      tagIdsByIdea: get().tagIdsByIdea,
    };
    const byName = new Map(get().tags.map((t) => [norm(t.name), t] as const));
    const seen = new Set<string>();
    const optimisticIds: string[] = [];
    const optimisticTags = [...get().tags];
    for (const raw of tagNames) {
      const key = norm(raw);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const existing = byName.get(key);
      if (existing) {
        optimisticIds.push(existing.id);
      } else {
        const tempId = `temp-${key}`;
        optimisticIds.push(tempId);
        optimisticTags.unshift({ id: tempId, name: raw.trim(), ideaCount: 1, lastUsedAt: Date.now() });
      }
    }
    const optimisticByIdea = new Map(get().tagIdsByIdea);
    optimisticByIdea.set(ideaId, optimisticIds);
    const optimisticById = new Map(optimisticTags.map((t) => [t.id, t] as const));
    set({ tags: optimisticTags, tagsById: optimisticById, tagIdsByIdea: optimisticByIdea });

    try {
      const catalog = await api.setIdeaTags({ ideaId, tagNames });
      set({ ...indexCatalog(catalog), loaded: true });
    } catch (err) {
      set(before); // roll back
      throw err;
    }
  },

  renameTag: async (tagId, name) => {
    const before = { tags: get().tags, tagsById: get().tagsById };
    // Optimistic simple rename (a merge, if the name collides, is reconciled from
    // the server response).
    const optimistic = get().tags.map((t) => (t.id === tagId ? { ...t, name: name.trim() } : t));
    set({ tags: optimistic, tagsById: new Map(optimistic.map((t) => [t.id, t] as const)) });
    try {
      const catalog = await api.renameIdeaTag({ tagId, name });
      set({ ...indexCatalog(catalog), loaded: true });
    } catch (err) {
      set(before);
      throw err;
    }
  },

  deleteTag: async (tagId) => {
    const before = {
      tags: get().tags,
      tagsById: get().tagsById,
      tagIdsByIdea: get().tagIdsByIdea,
    };
    // Optimistic: drop the tag from the catalog and from every idea.
    const optimistic = get().tags.filter((t) => t.id !== tagId);
    const optimisticByIdea = new Map<string, string[]>();
    for (const [ideaId, ids] of get().tagIdsByIdea) {
      optimisticByIdea.set(ideaId, ids.filter((id) => id !== tagId));
    }
    set({
      tags: optimistic,
      tagsById: new Map(optimistic.map((t) => [t.id, t] as const)),
      tagIdsByIdea: optimisticByIdea,
    });
    try {
      const catalog = await api.deleteIdeaTag({ tagId });
      set({ ...indexCatalog(catalog), loaded: true });
    } catch (err) {
      set(before);
      throw err;
    }
  },

  pinCollection: async (tagId) => {
    const before = get().collectionTagIds;
    if (before.includes(tagId)) return;
    set({ collectionTagIds: [...before, tagId] }); // optimistic: append
    try {
      const catalog = await api.pinCollection({ tagId });
      set({ ...indexCatalog(catalog), loaded: true });
    } catch (err) {
      set({ collectionTagIds: before });
      throw err;
    }
  },

  unpinCollection: async (tagId) => {
    const before = get().collectionTagIds;
    set({ collectionTagIds: before.filter((id) => id !== tagId) }); // optimistic
    try {
      const catalog = await api.unpinCollection({ tagId });
      set({ ...indexCatalog(catalog), loaded: true });
    } catch (err) {
      set({ collectionTagIds: before });
      throw err;
    }
  },

  reorderCollections: async (orderedTagIds) => {
    const before = get().collectionTagIds;
    set({ collectionTagIds: orderedTagIds }); // optimistic: apply the drop order
    try {
      const catalog = await api.reorderCollections({ tagIds: orderedTagIds });
      set({ ...indexCatalog(catalog), loaded: true });
    } catch (err) {
      set({ collectionTagIds: before });
      throw err;
    }
  },

  reset: () =>
    set({
      loaded: false,
      loading: false,
      tags: [],
      tagsById: new Map(),
      tagIdsByIdea: new Map(),
      collectionTagIds: [],
    }),
}));

/**
 * Which ideas from `ideaIds` survive the current tag filter. Multi-select is AND:
 * an idea passes only if it carries EVERY selected tag, so each added tag
 * narrows the list. An empty selection passes everything. Pure — pass the store's `tagIdsByIdea` in.
 */
export function ideaMatchesTagFilter(
  ideaId: string,
  selectedTagIds: string[],
  tagIdsByIdea: Map<string, string[]>,
): boolean {
  if (selectedTagIds.length === 0) return true;
  const ids = tagIdsByIdea.get(ideaId);
  if (!ids || ids.length === 0) return false;
  return selectedTagIds.every((sel) => ids.includes(sel));
}
